import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaPlus, FaEdit, FaTrash, FaSearch, FaBullhorn, FaCalendarAlt } from "react-icons/fa";
// import "./BlogPostCRUD.css"; // We will use inline styles or standard classes to match the theme

interface BlogPost {
  id?: number;
  title: string;
  content: string;
  created_at?: string;
  updated_at?: string;
  branch?: number;
}

const BlogPostCRUD: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [form, setForm] = useState<BlogPost>({ title: "", content: "" });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/Branches/posts/");
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`/Branches/posts/${editingId}/`, form);
      } else {
        await axios.post("/Branches/posts/", form);
      }
      fetchPosts();
      resetForm();
    } catch (error) {
      console.error("Error saving post:", error);
    }
  };

  const resetForm = () => {
    setForm({ title: "", content: "" });
    setEditingId(null);
    setShowModal(false);
  };

  const handleEdit = (post: BlogPost) => {
    setForm({ title: post.title, content: post.content });
    setEditingId(post.id || null);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Ma hubtaa inaad tirtirto ogeysiiskan?")) {
      try {
        await axios.delete(`/Branches/posts/${id}/`);
        fetchPosts();
      } catch (error) {
        console.error("Error deleting post:", error);
      }
    }
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="dashboard-content bg-light min-vh-100 p-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaBullhorn className="me-2 text-primary" />
            Maamulka Ogeysiisyada
          </h2>
          <p className="text-muted mb-0">Halkaan ka maamul wararka iyo ogeysiisyada waalidiinta.</p>
        </div>
        <button
          className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
          onClick={() => { resetForm(); setShowModal(true); }}
        >
          <FaPlus />
          <span>Ogeysiis Cusub</span>
        </button>
      </div>

      {/* Search and Stats */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-3">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <FaSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Raadi ogeysiis..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6 text-md-end mt-3 mt-md-0">
              <span className="text-muted me-3">
                <strong className="text-dark">{posts.length}</strong> Ogeysiis
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-5">
          <div className="bg-white rounded-circle d-inline-flex p-4 mb-3 shadow-sm">
            <FaBullhorn className="text-muted" size={32} />
          </div>
          <h5 className="text-muted">Lagama helin wax ogeysiis ah</h5>
          <p className="text-muted small">Ku dar ogeysiis cusub si uu halkan uga muuqdo.</p>
        </div>
      ) : (
        <div className="row g-4">
          {filteredPosts.map((post) => (
            <div className="col-md-6 col-lg-4" key={post.id}>
              <div className="card h-100 border-0 shadow-sm hover-shadow transition-all">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title fw-bold text-dark mb-0 text-truncate-2">{post.title}</h5>
                    <div className="dropdown">
                      <button className="btn btn-link text-muted p-0" data-bs-toggle="dropdown">
                        <i className="bi bi-three-dots-vertical"></i>
                      </button>
                    </div>
                  </div>
                  <p className="card-text text-muted mb-4 text-truncate-3" style={{ minHeight: '4.5rem' }}>
                    {post.content}
                  </p>
                  <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                    <small className="text-muted d-flex align-items-center">
                      <FaCalendarAlt className="me-1" />
                      {post.created_at ? new Date(post.created_at).toLocaleDateString() : 'N/A'}
                    </small>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(post)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(post.id!)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal Overlay */}
      {showModal && (
        <div className="modal-overlay position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex justify-content-center align-items-center" style={{ zIndex: 1050 }}>
          <div className="modal-dialog w-100" style={{ maxWidth: '500px' }}>
            <div className="modal-content bg-white rounded-3 shadow-lg">
              <div className="modal-header p-4 border-bottom">
                <h5 className="modal-title fw-bold">
                  {editingId ? 'Wax ka Bedel' : 'Abuur Ogeysiis Cusub'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label text-muted small fw-bold text-uppercase">Ciwaanka</label>
                    <input
                      type="text"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      placeholder="Geli ciwaanka ogeysiiska"
                      className="form-control form-control-lg"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small fw-bold text-uppercase">Nuxurka</label>
                    <textarea
                      name="content"
                      value={form.content}
                      onChange={handleChange}
                      placeholder="Qor faahfaahinta ogeysiiska..."
                      className="form-control"
                      rows={6}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer p-4 border-top bg-light rounded-bottom">
                  <button
                    type="button"
                    className="btn btn-light border me-2"
                    onClick={() => setShowModal(false)}
                  >
                    Jooji
                  </button>
                  <button type="submit" className="btn btn-primary px-4">
                    {editingId ? "Badbaadi Bedelka" : "Daabac"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .hover-shadow:hover {
          transform: translateY(-2px);
          box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
        }
        .transition-all {
          transition: all 0.3s ease;
        }
        .text-truncate-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .text-truncate-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default BlogPostCRUD;
